import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tabs,
  Tab,
  IconButton,
  Stack,
  Chip,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Task as TaskIcon,
  Event as MeetingIcon,
  InsertChart as AnalyticsIcon,
  Folder as DocumentIcon,
  Group as TeamIcon
} from '@mui/icons-material';
import { Case } from '../types';
import { TabPanel } from '@/components/TabPanel';
import { TaskManagement } from './TaskManagement';
import { MeetingScheduler } from './MeetingScheduler';
import { CaseAnalytics } from './CaseAnalytics';
import { DocumentManagement } from './DocumentManagement';
import { TeamCollaboration } from './TeamCollaboration';

interface CaseDetailsProps {
  caseData: Case | null;
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedCase: Case) => void;
}

export const CaseDetails: React.FC<CaseDetailsProps> = ({
  caseData,
  open,
  onClose,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!caseData) return null;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'closed':
        return 'success';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  const tabs = [
    { icon: <TaskIcon />, label: 'Tasks', component: TaskManagement },
    { icon: <MeetingIcon />, label: 'Meetings', component: MeetingScheduler },
    { icon: <AnalyticsIcon />, label: 'Analytics', component: CaseAnalytics },
    { icon: <DocumentIcon />, label: 'Documents', component: DocumentManagement },
    { icon: <TeamIcon />, label: 'Team', component: TeamCollaboration }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">
              {caseData.title}
            </Typography>
            <Chip
              label={caseData.status.replace('_', ' ')}
              color={getStatusColor(caseData.status)}
              size="small"
            />
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                id={`case-tab-${index}`}
                aria-controls={`case-tabpanel-${index}`}
                iconPosition="start"
                sx={{
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              />
            ))}
          </Tabs>
        </Box>

        {tabs.map((tab, index) => (
          <TabPanel key={index} value={activeTab} index={index}>
            {React.createElement(tab.component, {
              caseData,
              onUpdate
            })}
          </TabPanel>
        ))}
      </DialogContent>
    </Dialog>
  );
};